import DeliveryAddressInput from '@/components/front-office/DeliveryAddressInput';
import { isPhoneTooShort, isFrenchPhoneFormatInvalid } from '@/hooks/usePaymentValidation';

const EMAIL_REGEX = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

/**
 * Delivery information form component
 * @param {object} props
 * @param {object} props.cart - Cart object
 * @param {function} props.setCart - Function to update cart
 * @param {function} props.setOrderContextInput - Function to set order context input
 * @param {boolean} props.isSameInfo - Whether billing info matches delivery info
 * @param {function} props.onSameInfoToggle - Callback when same info checkbox is toggled
 */
const DeliveryInfoForm = ({ cart, setCart, setOrderContextInput, isSameInfo, onSameInfoToggle }) => {
	const validateEmail = (email) => {
		if (!email || email === '') return true;
		return EMAIL_REGEX.test(email);
	};

	const isEmailInvalid = cart?.billing_email !== '' && !validateEmail(cart?.billing_email);
	const isDeliveryPhoneTooShort = isPhoneTooShort(cart?.delivery_phone);
	const isDeliveryPhoneFormatInvalid = isFrenchPhoneFormatInvalid(cart?.delivery_phone);
	const isDeliveryPhoneInvalid = isDeliveryPhoneTooShort || isDeliveryPhoneFormatInvalid;

	return (
		<div className="shadow-lg px-2 pb-4 rounded-lg w-full bg-white lg:pb-2">
			<h3 className="text-center mb-4 text-md md:text-xl">Information de livraison</h3>

			<div className="grid grid-cols-2 w-full mb-2">
				<DeliveryAddressInput
					withoutValidationButton={true}
					setPropInput={setOrderContextInput}
					classes={'col-span-2 mb-4 shadow-lg py-1 px-4 border border-black rounded-full text-xs md:text-md'}
					withInputDiv={false}
					field={{
						id: 'delivery_address',
						name: 'delivery_address',
						type: 'text',
						labelFor: 'delivery_address',
						labelText: 'Adresse de livraison',
						placeholder: 'Adresse de livraison*',
						isRequired: true,
					}}
					isDisabled={true}
					extraInputAttributes={{ disabled: true }}
				/>

				<div className="mb-4 col-span-2 grid grid-cols-2 gap-2 w-full">
					<input
						type="text"
						id="delivery_lastname"
						name="delivery_lastname"
						placeholder="Nom*"
						className="shadow-lg py-1 px-4 border border-black rounded-full w-full text-xs md:text-md"
						onChange={(e) => setCart((prevState) => ({ ...prevState, delivery_lastname: e.target.value }))}
						required
					/>

					<input
						type="text"
						id="delivery_firstname"
						name="delivery_firstname"
						placeholder="Prenom*"
						className="shadow-lg py-1 px-4 border border-black rounded-full w-full text-xs md:text-md"
						onChange={(e) => setCart((prevState) => ({ ...prevState, delivery_firstname: e.target.value }))}
						required
					/>
				</div>

				<div className="mb-4 col-span-2 grid grid-cols-2 w-full">
					<div className="relative">
						{isDeliveryPhoneTooShort && (
							<p className="ml-2 mb-1 text-xs text-red-500 font-bold">Numéro trop court</p>
						)}
						{isDeliveryPhoneFormatInvalid && (
							<p className="ml-2 mb-1 text-xs text-red-500 font-bold">Format français invalide (0X XX XX XX XX)</p>
						)}
						<input
							type="tel"
							id="delivery_phone"
							name="delivery_phone"
							placeholder="Tel* (0X XX XX XX XX)"
							className={`shadow-lg py-1 px-4 rounded-full w-full text-xs md:text-md ${isDeliveryPhoneInvalid ? 'border border-red-500' : 'border border-black'}`}
							value={cart.delivery_phone}
							onChange={(e) => setCart((prevState) => ({ ...prevState, delivery_phone: e.target.value }))}
							required
						/>
					</div>

					<div className="relative">
						{isEmailInvalid && (
							<p className="ml-2 mb-1 text-xs text-red-500 font-bold">Format invalide</p>
						)}
						<input
							type="email"
							id="billing_email"
							name="billing_email"
							placeholder="Email*"
							className={`border ${isEmailInvalid ? 'border-red-500 py-1' : 'border-black py-2'} shadow-lg px-4 rounded-full w-full text-xs md:text-md`}
							onChange={(e) => setCart((prevState) => ({ ...prevState, billing_email: e.target.value }))}
							required
						/>
					</div>
				</div>

				<textarea
					id="delivery_message"
					name="delivery_message"
					placeholder="Commentaires / Etage / Digicode"
					className="col-span-2 shadow-lg py-1 px-4 border border-black rounded-full resize-none text-xs mb-2 md:text-md"
					onChange={(e) => setCart((prevState) => ({ ...prevState, delivery_message: e.target.value }))}
					rows="1"
				/>
			</div>

			<div className='flex items-center px-4 gap-2'>
				<input
					type='checkbox'
					id='same_billing_address'
					name='same_billing_address'
					onChange={(e) => onSameInfoToggle(e.target.checked)}
					checked={isSameInfo}
					className='h-4 w-4'
				/>
				<label htmlFor='same_billing_address' className='text-[12px] md:text-md'>
					Facturation identique aux informations de livraison
				</label>
			</div>
		</div>
	);
};

export default DeliveryInfoForm;

